/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import swal from "sweetalert";

import { saveAs } from "file-saver";
import Autocomplete from "@mui/material/Autocomplete";
import { useNavigate } from "react-router-dom";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Button from "@mui/material/Button";
import { alpha } from "@mui/material/styles";
import jsPDF from "jspdf";
import QRCode from "qrcode";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDDropzone from "components/MDDropzone";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";

const accent = {
  blue: "#1565c0",
  blueInternal: "#3949ab",
  green: "#2e7d32",
  red: "#c62828",
  slate: "#546e7a",
};

const infoAccentSx = (borderColor) => ({
  pl: 1.25,
  pr: 0.75,
  py: 0.45,
  borderLeft: "3px solid",
  borderLeftColor: borderColor,
  bgcolor: alpha(borderColor, 0.06),
  borderRadius: "0 6px 6px 0",
});

const accentCycle = [accent.blue, accent.green, accent.red, accent.slate];

function formatTanggalId(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "-"
    : d.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

function parseFotoAset(raw) {
  if (!raw) return [];
  try {
    const p = typeof raw === "string" ? JSON.parse(raw) : raw;
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function getAsetDetailUrl(id) {
  if (typeof window === "undefined" || id == null) return "";
  const base = window.location.origin.replace(/\/$/, "");
  const pub = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}${pub}/#/pages/aset/detail/${id}`;
}

/** URL foto pemegang — uid_RSS_Holder dari API (JOIN holder_id → users). */
function getHolderAvatarUrl(item) {
  if (!item || typeof item !== "object") return null;
  const p = item.pemegang;
  const uid =
    item.uid_RSS_Holder ??
    item.uid_RSS_holder ??
    p?.uid_RSS ??
    item.holder_uid_RSS ??
    item.holder_uid_rss;
  if (uid) {
    return `https://smartdh.rotendaokab.go.id/uploads/${uid}.jpg`;
  }
  const profil = p?.profil ?? item.holder_profil;
  if (profil) {
    return `${API_ESA_OFFICE_UPLOAD}${profil}`;
  }
  return null;
}

function AsetList() {
  const [menu, setMenu] = useState(null);
  const dropzoneRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [openQrAsetDialog, setOpenQrAsetDialog] = useState(false);
  const [qrAsetDialogItem, setQrAsetDialogItem] = useState(null);
  const [qrAsetPdfLoading, setQrAsetPdfLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [userId, setUserId] = useState(null);
  const [dinasId, setDinasId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserId(user.uid);
    setDinasId(user.dinas_id);
  }, []);

  const [form, setForm] = useState({
    kodeBarang: "",
    register: "",
    nama_barang: "",
    merk_type: "",
    no_identitas_barang: "",
    bahan: "",
    asal_perolehan: "",
    tahun_perolehan: "",
    ukuran_barang: "",
    satuan: "",
    keadaan_barang: "",
    jumlah_barang: "",
    harga_satuan: "",
    keterangan: "",
    jenis_barang: "",
    lokasi: "",
    holder_id: "",
    idAdmin: userId,
    idDinas: dinasId,
  });
  const [editId, setEditId] = useState(null);
  const [filterKeadaan, setFilterKeadaan] = useState("All");
  const [search, setSearch] = useState("");
  const [aset, setAset] = useState([]);
  const [selectedSatuan, setSelectedSatuan] = useState(null);
  const [selectedKeadaan, setSelectedKeadaan] = useState(null);
  const [selectedBahan, setSelectedBahan] = useState(null);
  const [selectedAsalPerolehan, setSelectedAsalPerolehan] = useState(null);
  const [selectedJenisBarang, setSelectedJenisBarang] = useState(null);
  const [selectedKecamatan, setSelectedKecamatan] = useState(null);
  const [selectedDesa, setSelectedDesa] = useState(null);
  const [selectedHolder, setSelectedHolder] = useState(null);
  const [desaData, setDesaData] = useState([]);
  const [userList, setUserList] = useState([]);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [files, setFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [showSizeWarning, setShowSizeWarning] = useState(false);

  const keadaanOptions = ["Baik", "Rusak Ringan", "Rusak Berat", "Hilang"];
  const satuanOptions = [
    "Unit",
    "Buah",
    "Set",
    "Lembar",
    "Meter",
    "Liter",
    "Kilogram",
    "Gram",
    "Ton",
    "Centimeter",
    "Millimeter",
    "Kilometer",
    "Meter Persegi",
    "Meter Kubik",
    "Centimeter Persegi",
    "Centimeter Kubik",
    "Mililiter",
    "Gallon",
    "Pint",
    "Quart",
    "Pak",
    "Box",
    "Kardus",
    "Botol",
    "Kaleng",
    "Dus",
    "Rim",
    "Kodi",
    "Gross",
    "Lusin",
    "Pasang",
    "Keping",
    "Helai",
    "Batang",
    "Gulung",
    "Meteran",
    "Yard",
    "Inch",
    "Feet",
    "Mile",
    "Acre",
    "Hektar",
    "Watt",
    "Volt",
    "Ampere",
    "Hertz",
    "Byte",
    "Kilobyte",
    "Megabyte",
    "Gigabyte",
    "Terabyte",
  ];
  const bahanOptions = ["Besi", "Kayu", "Plastik", "Kain", "Kaca"];
  const asalPerolehanOptions = [
    "Dana Hibah",
    "APBD",
    "Pengadaan/Pembelian",
    "Lainnya",
  ];
  const jenisBarangOptions = ["Barang Bergerak", "Barang Tidak Bergerak"];

  const fetchAset = () => {
    setLoading(true);
    axios
      .get(`${API_ESA_OFFICE}aset/dinas/${dinasId}`)
      .then((res) => {
        setAset(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const fetchDesa = async () => {
    try {
      const response = await axios.get(`${API_ESA_OFFICE}desa`);
      setDesaData(response.data || []);
    } catch (error) {
      console.error("Error fetching desa data:", error);
      setDesaData([]);
    }
  };

  const fetchUserList = async () => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}users/dinas/${dinasId}`
      );
      const formattedUsers = response.data.map((user) => ({
        value: user.user_id,
        label: user.nama,
        avatar: user.profil ? `${API_ESA_OFFICE_UPLOAD}${user.profil}` : null,
      }));
      setUserList(formattedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUserList([]);
    }
  };

  useEffect(() => {
    fetchDesa();
  }, []);

  useEffect(() => {
    if (dinasId) {
      fetchUserList();
      fetchAset();
    }
  }, [dinasId]);

  // Fungsi untuk menangani klik pada foto
  const handleImageClick = (images) => {
    setSelectedImages(images);
    setOpenImageDialog(true);
  };

  const getKeadaanChipColor = (keadaan) => {
    const k = (keadaan || "").toLowerCase();
    if (k.includes("baik")) return "success";
    if (k.includes("ringan")) return "warning";
    if (k.includes("berat") || k.includes("hilang")) return "error";
    return "default";
  };

  // Data baris, filter sesuai filterKeadaan dan search
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return aset
      .filter((item) =>
        filterKeadaan === "All" ? true : item.keadaan_barang === filterKeadaan
      )
      .filter((item) => {
        if (!q) return true;
        const hay = [
          item.nama_barang,
          item.kodeBarang,
          item.register,
          item.merk_type,
          item.no_identitas_barang,
          item.lokasi,
          item.nama_holder,
          item.nama_admin,
          item.keterangan,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .map((item) => ({ ...item }));
  }, [aset, filterKeadaan, search]);

  const existingFotoEditDialog = useMemo(
    () => (isEdit ? parseFotoAset(qrCodeData?.foto) : []),
    [isEdit, qrCodeData]
  );

  const handleOpenDialog = () => {
    setForm({
      kodeBarang: "",
      register: "",
      nama_barang: "",
      merk_type: "",
      no_identitas_barang: "",
      bahan: "",
      asal_perolehan: "",
      tahun_perolehan: "",
      ukuran_barang: "",
      satuan: "",
      keadaan_barang: "",
      jumlah_barang: "",
      harga_satuan: "",
      keterangan: "",
      jenis_barang: "",
      lokasi: "",
      idAdmin: userId,
      idDinas: dinasId,
    });
    setSelectedSatuan(null);
    setSelectedKeadaan(null);
    setSelectedBahan(null);
    setSelectedAsalPerolehan(null);
    setSelectedJenisBarang(null);
    setSelectedKecamatan(null);
    setSelectedDesa(null);
    setSelectedHolder(null);
    setIsEdit(false);
    setEditId(null);
    setQrCodeData(null);
    setFiles([]);
    setPreviewUrls([]);
    setTotalSize(0);
    setShowSizeWarning(false);
    setOpenDialog(true);
  };

  const handleEdit = (data) => {
    const satuanData = satuanOptions.find((s) => s === data.satuan);
    const keadaanData = keadaanOptions.find((k) => k === data.keadaan_barang);
    const bahanData = bahanOptions.find((b) => b === data.bahan);
    const asalPerolehanData = asalPerolehanOptions.find(
      (a) => a === data.asal_perolehan
    );
    const jenisBarangData = jenisBarangOptions.find(
      (j) => j === data.jenis_barang
    );

    // Parse lokasi untuk mendapatkan kecamatan dan desa
    let kecamatanData = null;
    let desaData = null;
    if (data.lokasi) {
      const lokasiParts = data.lokasi.split(" - ");
      if (lokasiParts.length === 2) {
        kecamatanData = lokasiParts[0];
        desaData = lokasiParts[1];
      }
    }

    // Find holder data
    const holderData = userList.find((user) => user.value === data.holder_id);

    setForm({
      kodeBarang: data.kodeBarang || "",
      register: data.register || "",
      nama_barang: data.nama_barang || "",
      merk_type: data.merk_type || "",
      no_identitas_barang: data.no_identitas_barang || "",
      bahan: data.bahan || "",
      asal_perolehan: data.asal_perolehan || "",
      tahun_perolehan: data.tahun_perolehan || "",
      ukuran_barang: data.ukuran_barang || "",
      satuan: data.satuan || "",
      keadaan_barang: data.keadaan_barang || "",
      jumlah_barang: data.jumlah_barang || "",
      harga_satuan: data.harga_satuan || "",
      keterangan: data.keterangan || "",
      jenis_barang: data.jenis_barang || "",
      lokasi: data.lokasi || "",
      holder_id: data.holder_id || "",
      idAdmin: userId,
      idDinas: dinasId,
    });
    setSelectedSatuan(satuanData);
    setSelectedKeadaan(keadaanData);
    setSelectedBahan(bahanData);
    setSelectedAsalPerolehan(asalPerolehanData);
    setSelectedJenisBarang(jenisBarangData);
    setSelectedKecamatan(kecamatanData);
    setSelectedDesa(desaData);
    setSelectedHolder(holderData);
    setIsEdit(true);
    setEditId(data.id);
    setQrCodeData(data);
    setFiles([]);
    setPreviewUrls([]);
    setTotalSize(0);
    setShowSizeWarning(false);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Data aset akan dihapus secara permanen!",
      icon: "warning",
      buttons: ["Batal", "Ya, hapus!"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await axios.delete(`${API_ESA_OFFICE}aset/${id}`);
          swal("Berhasil!", "Data aset berhasil dihapus", "success");
          fetchAset();
        } catch (err) {
          swal("Error!", "Gagal menghapus aset", "error");
        }
      }
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setForm({
      kodeBarang: "",
      register: "",
      nama_barang: "",
      merk_type: "",
      no_identitas_barang: "",
      bahan: "",
      asal_perolehan: "",
      tahun_perolehan: "",
      ukuran_barang: "",
      satuan: "",
      keadaan_barang: "",
      jumlah_barang: "",
      harga_satuan: "",
      keterangan: "",
      jenis_barang: "",
      lokasi: "",
      holder_id: "",
      idAdmin: userId,
      idDinas: dinasId,
    });
    setSelectedSatuan(null);
    setSelectedKeadaan(null);
    setSelectedBahan(null);
    setSelectedAsalPerolehan(null);
    setSelectedJenisBarang(null);
    setSelectedKecamatan(null);
    setSelectedDesa(null);
    setSelectedHolder(null);
    setIsEdit(false);
    setEditId(null);
    setQrCodeData(null);
    setFiles([]);
    setPreviewUrls([]);
    setTotalSize(0);
    setShowSizeWarning(false);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDropzoneChange = (files) => {
    const totalSizeInMB =
      files.reduce((acc, file) => acc + file.size, 0) / (1024 * 1024);
    setTotalSize(totalSizeInMB);
    setShowSizeWarning(totalSizeInMB > 15);
    setFiles(files);
  };

  const handleRemove = useCallback((file) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f !== file));
  }, []);

  const handleSubmit = async () => {
    try {
      const buildFormData = () => {
        const formData = new FormData();
        Object.keys(form).forEach((key) => {
          const val = form[key];
          formData.append(key, val != null && val !== "" ? val : "");
        });
        if (files && files.length > 0) {
          files.forEach((file) => {
            if (file instanceof Blob) formData.append("foto", file);
          });
        }
        return formData;
      };

      if (editId) {
        const formData = buildFormData();
        await axios.put(`${API_ESA_OFFICE}aset/${editId}`, formData);
      } else {
        const formData = buildFormData();
        const createRes = await axios.post(
          `${API_ESA_OFFICE}aset`,
          formData
        );
        setQrCodeData(createRes.data);
      }

      swal(
        "Berhasil!",
        editId ? "Data aset berhasil diperbarui" : "Data aset berhasil ditambahkan",
        "success"
      );
      handleCloseDialog();
      fetchAset();
    } catch (error) {
      console.error("Error:", error);
      swal("Error!", "Gagal menyimpan data aset", "error");
    }
  };

  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(menu)}
      onClose={closeMenu}
      keepMounted
    >
      <MenuItem
        selected={filterKeadaan === "All"}
        onClick={() => {
          setFilterKeadaan("All");
          closeMenu();
        }}
      >
        Semua
      </MenuItem>
      {keadaanOptions.map((keadaan) => (
        <MenuItem
          key={keadaan}
          selected={filterKeadaan === keadaan}
          onClick={() => {
            setFilterKeadaan(keadaan);
            closeMenu();
          }}
        >
          {keadaan}
        </MenuItem>
      ))}
    </Menu>
  );

  // Fungsi export Excel
  const handleExportExcel = () => {
    if (!aset.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Kode Barang",
      "Register",
      "Nama Barang",
      "Merk/Type",
      "No. Identitas Barang",
      "Bahan",
      "Asal Perolehan",
      "Tahun Perolehan",
      "Ukuran Barang",
      "Satuan",
      "Keadaan Barang",
      "Jumlah Barang",
      "Harga Satuan",
      "Jenis Barang",
      "Lokasi",
      "Pemegang Barang",
      "Admin",
      "Keterangan",
      "Tanggal Dibuat",
    ];

    let tableRows = "";
    aset.forEach((item, index) => {
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${item.kodeBarang || ""}</td>
          <td>${item.register || ""}</td>
          <td>${item.nama_barang || ""}</td>
          <td>${item.merk_type || ""}</td>
          <td>${item.no_identitas_barang || ""}</td>
          <td>${item.bahan || ""}</td>
          <td>${item.asal_perolehan || ""}</td>
          <td>${item.tahun_perolehan || ""}</td>
          <td>${item.ukuran_barang || ""}</td>
          <td>${item.satuan || ""}</td>
          <td>${item.keadaan_barang || ""}</td>
          <td>${item.jumlah_barang || ""}</td>
          <td>${
            item.harga_satuan
              ? `Rp ${parseInt(item.harga_satuan).toLocaleString("id-ID")}`
              : ""
          }</td>
          <td>${item.jenis_barang || ""}</td>
          <td>${item.lokasi || ""}</td>
          <td>${item.nama_holder || ""}</td>
          <td>${item.nama_admin || ""}</td>
          <td>${item.keterangan || ""}</td>
          <td>${
            item.createdAt
              ? new Date(item.createdAt).toLocaleDateString("id-ID")
              : ""
          }</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Data Aset</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Data Aset</h2>
          <p>Total Aset: ${aset.length} item</p>
          <p>Tanggal Export: ${new Date().toLocaleDateString("id-ID")}</p>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(blob, `aset-list-${new Date().toISOString().split("T")[0]}.xls`);
  };

  const handleViewDetail = (id) => {
    navigate(`/pages/aset/detail/${id}`);
  };

  const handleOpenQrAsetDialog = useCallback((payload) => {
    if (!payload?.qrcode) return;
    setQrAsetDialogItem(payload);
    setOpenQrAsetDialog(true);
  }, []);

  const handleCloseQrAsetDialog = useCallback(() => {
    setOpenQrAsetDialog(false);
    setQrAsetDialogItem(null);
  }, []);

  const handleDownloadQrAsetPdf = useCallback(async () => {
    if (!qrAsetDialogItem?.id) return;
    setQrAsetPdfLoading(true);
    try {
      const detailUrl = getAsetDetailUrl(qrAsetDialogItem.id);
      const qrDataUrl = await QRCode.toDataURL(detailUrl, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 512,
        color: { dark: "#000000", light: "#ffffff" },
      });

      const pdfW = 92;
      const margin = 9;
      const innerW = pdfW - 2 * margin;
      const nama = qrAsetDialogItem.nama_barang || "-";

      const measure = new jsPDF({ unit: "mm", format: [pdfW, 300] });
      const namaLines = measure.splitTextToSize(nama, innerW);

      let estBottom = margin + 7 + namaLines.length * 5 + 2;
      if (qrAsetDialogItem.kodeBarang) estBottom += 6;
      estBottom += 6;
      estBottom += 62;
      estBottom += 4 + 8;
      const pdfH = Math.max(Math.ceil(estBottom + margin), 108);

      const doc = new jsPDF({
        unit: "mm",
        format: [pdfW, pdfH],
        orientation: "portrait",
      });

      let y = margin;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(21, 101, 192);
      doc.text("Esa Office", margin, y);
      doc.setTextColor(33, 33, 33);
      doc.text("QR Code Aset", pdfW - margin, y, { align: "right" });
      y += 7;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(55, 55, 55);
      doc.text(namaLines, margin, y);
      y += namaLines.length * 5 + 2;

      if (qrAsetDialogItem.kodeBarang) {
        doc.setFontSize(8.5);
        doc.setTextColor(90, 90, 90);
        doc.text(`Kode barang: ${qrAsetDialogItem.kodeBarang}`, margin, y);
        y += 6;
      }

      doc.setFontSize(8.5);
      doc.setTextColor(110, 110, 110);
      doc.text("Scan QR Code berikut untuk membuka detail aset", margin, y);
      y += 6;

      const areaTop = y;
      const areaH = 62;
      doc.setFillColor(250, 250, 250);
      doc.rect(margin, areaTop, innerW, areaH, "F");
      doc.setDrawColor(224, 224, 224);
      doc.rect(margin, areaTop, innerW, areaH, "S");

      const qrMm = 48;
      const imgX = margin + (innerW - qrMm) / 2;
      const imgY = areaTop + (areaH - qrMm) / 2;
      doc.setFillColor(255, 255, 255);
      doc.rect(imgX - 2, imgY - 2, qrMm + 4, qrMm + 4, "F");
      doc.setDrawColor(200, 200, 200);
      doc.rect(imgX - 2, imgY - 2, qrMm + 4, qrMm + 4, "S");
      doc.addImage(qrDataUrl, "PNG", imgX, imgY, qrMm, qrMm);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(100, 100, 100);
      const footer = `${new Date().toLocaleString("id-ID")}`;
      doc.text(footer, margin, pdfH - margin);

      const safe = (nama || "aset")
        .replace(/[^a-zA-Z0-9-_]/g, "_")
        .slice(0, 42);
      doc.save(`qr-aset-${safe}-${qrAsetDialogItem.id}.pdf`);
    } catch (e) {
      swal("Error", `Gagal membuat PDF: ${e?.message || ""}`, "error");
    } finally {
      setQrAsetPdfLoading(false);
    }
  }, [qrAsetDialogItem]);

  // Mendapatkan opsi kecamatan unik dari data desa
  const kecamatanOptions = useMemo(() => {
    const kecamatans = [...new Set(desaData.map((item) => item.kecamatan))];
    return kecamatans.sort();
  }, [desaData]);

  // Mendapatkan opsi desa berdasarkan kecamatan yang dipilih
  const desaOptions = useMemo(() => {
    if (!selectedKecamatan) return [];
    const desas = desaData
      .filter((item) => item.kecamatan === selectedKecamatan)
      .map((item) => item.desa);
    return desas.sort();
  }, [desaData, selectedKecamatan]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox my={3}>
        <Card sx={{ overflow: "hidden" }}>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <MDTypography variant="h6" color="white">
              Daftar Aset
            </MDTypography>
            <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <MDButton
                variant={menu ? "contained" : "outlined"}
                color="light"
                onClick={openMenu}
                size="small"
                sx={
                  menu
                    ? { color: "info.main" }
                    : { color: "white", borderColor: "rgba(255,255,255,0.6)" }
                }
              >
                Filter Keadaan&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderMenu}
              <MDButton
                variant="outlined"
                color="light"
                onClick={handleExportExcel}
                size="small"
                sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)" }}
              >
                <Icon>description</Icon>
                &nbsp;Export Excel
              </MDButton>
            </MDBox>
          </MDBox>
          <MDBox pt={3} pb={3} px={3}>
            <Grid container spacing={2} mb={2} alignItems="flex-end">
              <Grid item xs={12} md={7} lg={8}>
                <TextField
                  label="Cari nama barang, kode, register, lokasi, pemegang..."
                  variant="standard"
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={5} lg={4}>
                <MDBox
                  display="flex"
                  justifyContent={{ xs: "stretch", md: "flex-end" }}
                  alignItems="center"
                  width="100%"
                >
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleOpenDialog}
                    sx={{ minWidth: { xs: "100%", md: 160 } }}
                  >
                    Tambah Aset
                  </MDButton>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="body2" color="text.secondary">
                  Menampilkan{" "}
                  <strong>{filteredRows.length}</strong> dari{" "}
                  <strong>{aset.length}</strong> aset
                  {filterKeadaan !== "All" ? (
                    <>
                      {" "}
                      · filter: <strong>{filterKeadaan}</strong>
                    </>
                  ) : null}
                </MDTypography>
              </Grid>
            </Grid>
            {loading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
              >
                <CircularProgress size={28} />
                <MDTypography variant="body2" color="text" ml={2}>
                  Memuat data aset…
                </MDTypography>
              </MDBox>
            ) : filteredRows.length === 0 ? (
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={4}
              >
                <MDTypography variant="h6" color="text.secondary">
                  {search.trim()
                    ? "Tidak ada aset yang cocok dengan pencarian"
                    : "Belum ada data aset"}
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary">
                  {search.trim()
                    ? "Coba kata kunci lain atau kosongkan pencarian"
                    : "Tambah aset baru dengan tombol di atas"}
                </MDTypography>
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                {filteredRows.map((item, index) => {
                  const fotoArray = parseFotoAset(item.foto);
                  const holderAvatarUrl = getHolderAvatarUrl(item);
                  const cardFields = [
                    { label: "Kode barang", value: item.kodeBarang },
                    { label: "Register", value: item.register },
                    { label: "Merk / type", value: item.merk_type },
                    { label: "No. identitas", value: item.no_identitas_barang },
                    { label: "Bahan", value: item.bahan },
                    { label: "Asal perolehan", value: item.asal_perolehan },
                    { label: "Tahun perolehan", value: item.tahun_perolehan },
                    { label: "Ukuran", value: item.ukuran_barang },
                    { label: "Satuan", value: item.satuan },
                    { label: "Keadaan", value: item.keadaan_barang },
                    { label: "Jumlah", value: item.jumlah_barang },
                    {
                      label: "Harga satuan",
                      value: item.harga_satuan
                        ? `Rp ${parseInt(item.harga_satuan, 10).toLocaleString("id-ID")}`
                        : "-",
                    },
                    { label: "Jenis barang", value: item.jenis_barang || "-" },
                    { label: "Lokasi", value: item.lokasi || "-" },
                    { label: "Pemegang", value: item.nama_holder || "-" },
                    { label: "Admin", value: item.nama_admin || "-" },
                    { label: "Keterangan", value: item.keterangan || "-" },
                    {
                      label: "Tanggal dibuat",
                      value: formatTanggalId(item.createdAt),
                    },
                  ];
                  return (
                    <Grid item xs={12} sm={6} lg={4} key={item.id}>
                      <Card
                        sx={{
                          p: 2,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                          transition: "box-shadow 0.2s, transform 0.2s",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          gap={1}
                          mb={1}
                        >
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="bold"
                          >
                            #{index + 1}
                          </MDTypography>
                        </MDBox>
                        <MDTypography
                          variant="body1"
                          fontWeight="bold"
                          sx={{
                            lineHeight: 1.35,
                            mb: 1,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {item.nama_barang || "-"}
                        </MDTypography>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexWrap="wrap"
                          mb={1.5}
                        >
                          {item.keadaan_barang ? (
                            <Chip
                              label={item.keadaan_barang}
                              color={getKeadaanChipColor(item.keadaan_barang)}
                              size="small"
                              variant="filled"
                            />
                          ) : null}
                          {item.jenis_barang ? (
                            <Chip
                              label={item.jenis_barang}
                              size="small"
                              variant="outlined"
                            />
                          ) : null}
                        </MDBox>
                        <Grid container spacing={1} sx={{ flex: 1 }}>
                          {cardFields.map((f, i) => (
                            <Grid item xs={12} sm={6} key={`${item.id}-${f.label}`}>
                              <MDBox sx={infoAccentSx(accentCycle[i % accentCycle.length])}>
                                <MDTypography
                                  variant="caption"
                                  sx={{
                                    color: accentCycle[i % accentCycle.length],
                                    fontWeight: 600,
                                    fontSize: "0.65rem",
                                    display: "block",
                                    mb: 0.15,
                                  }}
                                >
                                  {f.label}
                                </MDTypography>
                                {f.label === "Pemegang" ? (
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    minWidth={0}
                                  >
                                    <MDBox
                                      position="relative"
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <MDBox
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                          position: "absolute",
                                          inset: 0,
                                          width: 32,
                                          height: 32,
                                          borderRadius: "50%",
                                          backgroundColor: "grey.200",
                                          color: "grey.500",
                                        }}
                                      >
                                        <PersonIcon sx={{ fontSize: 20 }} />
                                      </MDBox>
                                      {holderAvatarUrl ? (
                                        <MDBox
                                          component="img"
                                          src={holderAvatarUrl}
                                          alt=""
                                          sx={{
                                            position: "absolute",
                                            inset: 0,
                                            width: 32,
                                            height: 32,
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                          }}
                                        />
                                      ) : null}
                                    </MDBox>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: "block",
                                        lineHeight: 1.35,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {f.value !== undefined &&
                                      f.value !== null &&
                                      String(f.value).trim() !== ""
                                        ? f.value
                                        : "-"}
                                    </MDTypography>
                                  </MDBox>
                                ) : (
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: "block",
                                      lineHeight: 1.35,
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {f.value !== undefined &&
                                    f.value !== null &&
                                    String(f.value).trim() !== ""
                                      ? f.value
                                      : "-"}
                                  </MDTypography>
                                )}
                              </MDBox>
                            </Grid>
                          ))}
                        </Grid>
                        <MDBox
                          display="flex"
                          alignItems="stretch"
                          gap={1}
                          p={1}
                          mt={1.5}
                          sx={{
                            borderRadius: 1,
                            bgcolor: "grey.50",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <MDBox flex={1} minWidth={0}>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              display="block"
                              mb={0.5}
                            >
                              Foto
                            </MDTypography>
                            <MDBox display="flex" gap={0.75} flexWrap="wrap">
                              {fotoArray.length === 0 ? (
                                <MDTypography variant="caption" color="text.secondary">
                                  Belum ada foto
                                </MDTypography>
                              ) : (
                                <>
                                  {fotoArray.slice(0, 3).map((foto, fi) => (
                                    <MDBox
                                      key={fi}
                                      component="img"
                                      src={`${API_ESA_OFFICE_UPLOAD}${foto}`}
                                      alt=""
                                      sx={{
                                        width: 44,
                                        height: 44,
                                        objectFit: "cover",
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        flexShrink: 0,
                                      }}
                                      onClick={() => handleImageClick(fotoArray)}
                                    />
                                  ))}
                                  {fotoArray.length > 3 ? (
                                    <MDBox
                                      sx={{
                                        width: 44,
                                        height: 44,
                                        bgcolor: "grey.200",
                                        borderRadius: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => handleImageClick(fotoArray)}
                                    >
                                      <MDTypography variant="caption" fontWeight="bold">
                                        +{fotoArray.length - 3}
                                      </MDTypography>
                                    </MDBox>
                                  ) : null}
                                </>
                              )}
                            </MDBox>
                          </MDBox>
                          <MDBox
                            flexShrink={0}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              pl: 1,
                              borderLeft: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              fontWeight="medium"
                              display="block"
                              mb={0.5}
                              textAlign="center"
                            >
                              QR
                            </MDTypography>
                            {item.qrcode ? (
                              <Tooltip title="Lihat QR & unduh PDF">
                                <MDBox
                                  component="img"
                                  src={`${API_ESA_OFFICE_UPLOAD}${item.qrcode}`}
                                  alt="QR"
                                  sx={{
                                    width: 52,
                                    height: 52,
                                    objectFit: "contain",
                                    cursor: "pointer",
                                    borderRadius: 1,
                                  }}
                                  onClick={() => handleOpenQrAsetDialog(item)}
                                />
                              </Tooltip>
                            ) : (
                              <MDTypography variant="caption" color="text.secondary">
                                …
                              </MDTypography>
                            )}
                          </MDBox>
                        </MDBox>
                        <MDBox
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                          gap={0.5}
                          pt={1.5}
                          mt="auto"
                          sx={{ borderTop: 1, borderColor: "divider" }}
                        >
                          <Tooltip title="Detail">
                            <IconButton
                              color="success"
                              onClick={() => handleViewDetail(item.id)}
                              size="small"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              color="info"
                              onClick={() => handleEdit(item)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Hapus">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(item.id)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </MDBox>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </MDBox>
        </Card>
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>{isEdit ? "Edit Aset" : "Tambah Aset Baru"}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Kode Barang"
                  name="kodeBarang"
                  value={form.kodeBarang}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Register"
                  name="register"
                  value={form.register}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  label="Nama Barang"
                  name="nama_barang"
                  value={form.nama_barang}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Merk/Type"
                  name="merk_type"
                  value={form.merk_type}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="No. Identitas Barang"
                  name="no_identitas_barang"
                  value={form.no_identitas_barang}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={bahanOptions}
                  value={selectedBahan}
                  onChange={(event, newValue) => {
                    setSelectedBahan(newValue);
                    setForm({ ...form, bahan: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Bahan"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={asalPerolehanOptions}
                  value={selectedAsalPerolehan}
                  onChange={(event, newValue) => {
                    setSelectedAsalPerolehan(newValue);
                    setForm({ ...form, asal_perolehan: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Asal Perolehan"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Tahun Perolehan"
                  name="tahun_perolehan"
                  value={form.tahun_perolehan}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Ukuran Barang"
                  name="ukuran_barang"
                  value={form.ukuran_barang}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={satuanOptions}
                  value={selectedSatuan}
                  onChange={(event, newValue) => {
                    setSelectedSatuan(newValue);
                    setForm({ ...form, satuan: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Satuan"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={keadaanOptions}
                  value={selectedKeadaan}
                  onChange={(event, newValue) => {
                    setSelectedKeadaan(newValue);
                    setForm({ ...form, keadaan_barang: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Keadaan Barang"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={jenisBarangOptions}
                  value={selectedJenisBarang}
                  onChange={(event, newValue) => {
                    setSelectedJenisBarang(newValue);
                    setForm({ ...form, jenis_barang: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Jenis Barang"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={kecamatanOptions}
                  value={selectedKecamatan}
                  onChange={(event, newValue) => {
                    setSelectedKecamatan(newValue);
                    setSelectedDesa(null); // Reset desa when kecamatan changes
                    setForm({ ...form, lokasi: "" }); // Reset lokasi when kecamatan changes
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Kecamatan"
                      required
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={desaOptions}
                  value={selectedDesa}
                  onChange={(event, newValue) => {
                    setSelectedDesa(newValue);
                    const lokasi =
                      selectedKecamatan && newValue ? `${newValue}` : "";
                    setForm({ ...form, lokasi });
                  }}
                  disabled={!selectedKecamatan}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Desa"
                      required
                      fullWidth
                      helperText={
                        !selectedKecamatan
                          ? "Pilih Kecamatan terlebih dahulu"
                          : ""
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={userList}
                  value={selectedHolder}
                  onChange={(event, newValue) => {
                    setSelectedHolder(newValue);
                    setForm({ ...form, holder_id: newValue?.value || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Pemegang Barang"
                      required
                      fullWidth
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={option.label}
                        avatar={
                          option.avatar ? (
                            <img
                              src={option.avatar}
                              alt={option.label}
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                              }}
                            />
                          ) : null
                        }
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <li {...props}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {option.avatar && (
                          <img
                            src={option.avatar}
                            alt={option.label}
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                            }}
                          />
                        )}
                        <span>{option.label}</span>
                      </div>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Jumlah Barang"
                  name="jumlah_barang"
                  value={form.jumlah_barang}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Harga Satuan"
                  name="harga_satuan"
                  value={form.harga_satuan}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  label="Keterangan"
                  name="keterangan"
                  value={form.keterangan}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
              {existingFotoEditDialog.length > 0 && (
                <Grid item xs={12}>
                  <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
                    Foto saat ini
                  </MDTypography>
                  <MDBox display="flex" flexWrap="wrap" gap={1.25}>
                    {existingFotoEditDialog.map((fotoPath, fi) => (
                      <MDBox
                        key={`${fotoPath}-${fi}`}
                        component="img"
                        src={`${API_ESA_OFFICE_UPLOAD}${fotoPath}`}
                        alt={`Foto ${fi + 1}`}
                        sx={{
                          width: 96,
                          height: 96,
                          objectFit: "cover",
                          borderRadius: 1,
                          cursor: "pointer",
                          border: "1px solid",
                          borderColor: "divider",
                          "&:hover": { opacity: 0.92 },
                        }}
                        onClick={() => handleImageClick(existingFotoEditDialog)}
                      />
                    ))}
                  </MDBox>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={1}
                  >
                    Klik gambar untuk melihat ukuran penuh. Unggah foto baru di
                    bawah untuk menambah ke daftar (jika belum mencapai batas
                    maksimal).
                  </MDTypography>
                </Grid>
              )}
              <Grid item xs={12}>
                {useMemo(
                  () => (
                    <MDDropzone
                      options={{
                        addRemoveLinks: true,
                        uploadMultiple: true,
                        maxFilesize: 5,
                        maxFiles: 5,
                        maxTotalFilesize: 15,
                        acceptedFiles: ".jpeg,.jpg,.png",
                        autoProcessQueue: false,
                        dictDefaultMessage:
                          "Klik atau seret gambar ke sini untuk mengunggah",
                        dictFileTooBig:
                          "Gambar terlalu besar ({{filesize}}MB). Maksimal ukuran file: {{maxFilesize}}MB",
                        dictTotalFilesTooBig:
                          "Total ukuran gambar melebihi batas maksimal (15MB)",
                        dictInvalidFileType:
                          "Format file tidak didukung. Hanya JPG, JPEG, dan PNG yang diperbolehkan",
                        dictResponseError:
                          "Terjadi kesalahan saat mengunggah gambar",
                        dictCancelUpload: "Batalkan unggah",
                        dictUploadCanceled: "Unggah dibatalkan",
                        dictCancelUploadConfirmation:
                          "Apakah Anda yakin ingin membatalkan unggah?",
                        dictRemoveFile: "Hapus gambar",
                        dictMaxFilesExceeded:
                          "Anda tidak dapat mengunggah gambar lagi",
                      }}
                      onChange={handleDropzoneChange}
                      ref={dropzoneRef}
                    />
                  ),
                  []
                )}
              </Grid>
              {files.length > 0 && (
                <MDBox mt={2}>
                  <MDTypography variant="h6" mb={2}>
                    Daftar File yang Akan Diunggah
                  </MDTypography>
                  <DataTable
                    table={{
                      columns: [
                        { Header: "Nama File", accessor: "name" },
                        {
                          Header: "Ukuran",
                          accessor: "size",
                          Cell: ({ value }) =>
                            `${(value / 1024 / 1024).toFixed(2)} MB`,
                        },
                        {
                          Header: "Tipe",
                          accessor: "type",
                          Cell: ({ value }) =>
                            value.split("/")[1].toUpperCase(),
                        },
                      ],
                      rows: files.map((file) => ({
                        name: file.name,
                        size: file.size,
                        type: file.type,
                      })),
                    }}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              )}
              <Grid item xs={12}>
                {isEdit && (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    mt={2}
                  >
                    <MDTypography variant="h6" mb={2}>
                      QR Code Aset
                    </MDTypography>
                    {qrCodeData?.qrcode ? (
                      <Card>
                        <MDBox
                          p={2}
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <Tooltip title="Lihat QR & unduh PDF">
                            <MDBox
                              component="img"
                              src={`${API_ESA_OFFICE_UPLOAD}${qrCodeData.qrcode}`}
                              alt="QR Code"
                              sx={{
                                width: 200,
                                height: 200,
                                objectFit: "contain",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                handleOpenQrAsetDialog({
                                  id: editId,
                                  nama_barang: form.nama_barang,
                                  kodeBarang: form.kodeBarang,
                                  qrcode: qrCodeData.qrcode,
                                })
                              }
                            />
                          </Tooltip>
                          <MDTypography variant="caption" color="text" mt={1}>
                            Scan QR Code untuk melihat detail aset
                          </MDTypography>
                        </MDBox>
                      </Card>
                    ) : (
                      <MDTypography variant="caption" color="text">
                        QR Code tidak tersedia
                      </MDTypography>
                    )}
                  </MDBox>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDialog}>
              Batal
            </MDButton>
            <MDButton color="info" variant="contained" onClick={handleSubmit}>
              Simpan
            </MDButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openQrAsetDialog}
          onClose={handleCloseQrAsetDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6">Aset – QR Code</MDTypography>
              <IconButton onClick={handleCloseQrAsetDialog} size="small">
                <CloseIcon />
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            {qrAsetDialogItem && (
              <MDBox pt={1} pb={2} px={1}>
                <MDBox mb={3}>
                  <MDTypography
                    variant="body2"
                    color="text"
                    sx={{ mb: 1, fontWeight: 500 }}
                  >
                    {qrAsetDialogItem.nama_barang || "-"}
                  </MDTypography>
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                  >
                    {qrAsetDialogItem.kodeBarang ? (
                      <Chip
                        label={`Kode: ${qrAsetDialogItem.kodeBarang}`}
                        size="small"
                        variant="outlined"
                      />
                    ) : null}
                    <MDTypography variant="caption" color="text.secondary">
                      Detail aset
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                  p={2}
                  sx={{
                    borderRadius: 2,
                    bgcolor: "grey.50",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <MDTypography variant="caption" color="text.secondary">
                    Scan QR Code berikut untuk membuka detail aset
                  </MDTypography>
                  <MDBox
                    sx={{
                      p: 2,
                      bgcolor: "white",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: 1,
                    }}
                  >
                    <MDBox
                      component="img"
                      src={`${API_ESA_OFFICE_UPLOAD}${qrAsetDialogItem.qrcode}`}
                      alt="QR Code Aset"
                      sx={{ width: 220, height: 220, objectFit: "contain" }}
                    />
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <LinkIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <MDTypography
                      variant="caption"
                      color="primary"
                      sx={{
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() =>
                        window.open(
                          getAsetDetailUrl(qrAsetDialogItem.id),
                          "_blank"
                        )
                      }
                    >
                      Buka halaman detail
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              pt: 1,
              gap: 1,
              flexWrap: "wrap",
              bgcolor: "background.paper",
            }}
          >
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleDownloadQrAsetPdf}
              disabled={qrAsetPdfLoading || !qrAsetDialogItem?.id}
              sx={{
                bgcolor: "grey.800",
                color: "#fff",
                boxShadow: 2,
                "&:hover": { bgcolor: "grey.900" },
                "& .MuiButton-startIcon": { color: "#fff" },
                "&.Mui-disabled": {
                  bgcolor: "grey.400",
                  color: "grey.100",
                },
              }}
            >
              {qrAsetPdfLoading ? "Membuat PDF…" : "Download PDF"}
            </Button>
            <Button
              variant="contained"
              onClick={handleCloseQrAsetDialog}
              sx={{
                bgcolor: "primary.main",
                color: "#fff",
                boxShadow: 2,
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              Tutup
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog untuk preview foto */}
        <Dialog
          open={openImageDialog}
          onClose={() => setOpenImageDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">Preview Foto</MDTypography>
              <MDButton
                color="secondary"
                onClick={() => setOpenImageDialog(false)}
                size="small"
              >
                Tutup
              </MDButton>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <Grid container spacing={2}>
                {selectedImages.map((foto, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <MDBox p={2}>
                        <img
                          src={`${API_ESA_OFFICE_UPLOAD}${foto}`}
                          alt={`Foto ${index + 1}`}
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            borderRadius: "8px",
                          }}
                        />
                        <MDBox mt={1}>
                          <MDTypography variant="caption" color="text">
                            Foto {index + 1}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          </DialogContent>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AsetList;
